import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes,
  faSpinner,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faInfoCircle,
  faCalendarAlt,
  faChartLine,
  faAddressCard,
  faPalette,
  faEye,
  faImage,
  faMusic
} from '@fortawesome/free-solid-svg-icons';
import styles from './EPKBuilder.module.css';

// Step components
import BasicInfoStep from './BasicInfoStep';
import PhotosStep from './PhotosStep';
import MusicVideosStep from './MusicVideosStep';
import PerformanceStep from './PerformanceStep';
import StatsStep from './StatsStep';
import ContactStep from './ContactStep';
import ThemeStep from './ThemeStep';
import PreviewStep from './PreviewStep';

// Helper function to check if value is an object
const isObject = (item) => {
  return (item && typeof item === 'object' && !Array.isArray(item));
};

// Deep merge utility function
const deepMerge = (target, source) => {
  const output = {...target};
  
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        // For arrays, prefer source array if it has items
        if (Array.isArray(source[key])) {
          output[key] = source[key].length > 0 ? [...source[key]] : target[key];
        } else {
          // For primitive values, use source if it exists
          output[key] = source[key] !== undefined ? source[key] : target[key];
        }
      }
    });
  }
  
  return output;
};

const EPKBuilder = ({ 
  isOpen, 
  onClose, 
  userData, 
  mode = 'create', 
  existingEPK = null,
  onSave,
  authToken
}) => {
  // Steps of the EPK creation process
  const steps = [
    { id: 'basic', name: 'Basic Info', icon: faInfoCircle },
    { id: 'photos', name: 'Photos & Logo', icon: faImage },
    { id: 'music', name: 'Music & Videos', icon: faMusic },
    { id: 'performances', name: 'Performance History', icon: faCalendarAlt },
    { id: 'stats', name: 'Stats & Press', icon: faChartLine },
    { id: 'contact', name: 'Contact Info', icon: faAddressCard },
    { id: 'theme', name: 'Theme & Layout', icon: faPalette },
    { id: 'preview', name: 'Preview & Publish', icon: faEye }
  ];

  // NEW: Create a ref to store file objects that need to persist between steps
  const fileStorageRef = useRef({
    photos: {},
    logo: null
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [errors, setErrors] = useState({});
  
  // Initialize EPK data
  const defaultEPKData = {
    // Basic Info
    title: 'My Electronic Press Kit',
    artistName: '',
    artistType: '',
    location: {
      city: '',
      state: '',
      country: ''
    },
    genres: [],
    activeSince: '',
    shortBio: '',
    longBio: '',
    
    // Photos & Logo
    photos: [],
    logo: null,
    thumbnail: null,
    
    // Music & Videos
    music: {
      spotify: [],
      appleMusic: [],
      soundcloud: []
    },
    videos: [],
    
    // Performance History
    performances: [
      { name: '', year: '', city: '', isHeadliner: false, audienceSize: '' }
    ],
    
    // Stats & Press
    stats: {
      spotify: {
        monthlyListeners: '',
        totalStreams: ''
      },
      youtube: {
        totalViews: '',
        topVideoViews: ''
      },
      instagram: '',
      tiktok: '',
      twitter: '',
      facebook: ''
    },
    pressQuotes: [
      { quote: '', source: '', link: '' }
    ],
    pressCoverage: [
      { title: '', publication: '', date: '', link: '' }
    ],
    
    // Contact Info
    contact: {
      name: '',
      role: '',
      email: '',
      phone: '',
      website: ''
    },
    socialLinks: {
      spotify: '',
      appleMusic: '',
      instagram: '',
      facebook: '',
      twitter: '',
      tiktok: '',
      youtube: '',
      soundcloud: '',
      linkedin: ''
    },
    
    // Theme & Layout
    theme: {
      template: 'minimal',
      accentColor: '#E2384D'
    },
    
    // Metadata
    isActive: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    userId: userData?.userId || '',
    version: '1.0'
  };

  // Pre-fill userData if available
  if (userData) {
    defaultEPKData.artistName = userData.fullName || '';
    defaultEPKData.location.city = userData.city || '';
    defaultEPKData.location.state = userData.state || '';
    defaultEPKData.location.country = userData.country || '';
    defaultEPKData.shortBio = userData.bio || '';
    defaultEPKData.contact.name = userData.fullName || '';
    defaultEPKData.contact.email = userData.email || '';
    defaultEPKData.socialLinks.spotify = userData.socialLinks?.spotifyUrl || '';
    defaultEPKData.socialLinks.appleMusic = userData.socialLinks?.appleMusicUrl || '';
    defaultEPKData.socialLinks.instagram = userData.socialLinks?.instagramUrl || '';
    defaultEPKData.socialLinks.facebook = userData.socialLinks?.facebookUrl || '';
    defaultEPKData.socialLinks.twitter = userData.socialLinks?.twitterUrl || '';
    defaultEPKData.socialLinks.youtube = userData.socialLinks?.youtubeUrl || '';
    defaultEPKData.socialLinks.linkedin = userData.socialLinks?.linkedInUrl || '';
  }

  // Initialize epkData with proper defaults, merging with existingEPK if in edit mode
  const [epkData, setEPKData] = useState(() => {
    console.log("Initializing EPK data with mode:", mode);
    console.log("Existing EPK data:", existingEPK);
    
    if (mode === 'edit' || mode === 'view') {
      if (existingEPK) {
        // Deep merge existingEPK with defaultEPKData for edit/view mode
        return deepMerge(defaultEPKData, existingEPK);
      }
    }
    
    // For create mode or fallback
    return defaultEPKData;
  });

  // Debug log epkData on changes
  useEffect(() => {
    console.log("Current EPK Data:", epkData);
  }, [epkData]);
  
  // Set up autosave timer
  useEffect(() => {
    if (mode !== 'view') {
      // Set up autosave every 30 seconds
      const autoSaveTimer = setInterval(() => {
        if (currentStep > 0) { // Don't autosave immediately on first step
          handleAutoSave();
        }
      }, 30000); // 30 seconds
      
      return () => {
        clearInterval(autoSaveTimer);
      };
    }
  }, [mode, currentStep]);
  
  // Handle auto save functionality
  const handleAutoSave = async () => {
    if (mode === 'view' || saving) return;
    
    try {
      setAutoSaving(true);
      
      // Create a copy of current EPK data with updated timestamp
      const updatedEPK = {
        ...epkData,
        updatedAt: new Date().toISOString(),
        // Don't set isActive yet unless it was already active
        isActive: epkData.isActive
      };
      
      // Call the onSave function passed from parent
      if (onSave) {
        await onSave(updatedEPK, false); // false means not publishing, just saving
      }
      
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error auto-saving EPK:', error);
    } finally {
      setAutoSaving(false);
    }
  };

  
  
  // Save and publish the EPK
  const handleSaveAndPublish = async () => {
    try {
      // Check if already saving to prevent multiple clicks
      if (saving) return;
      // Set saving state to true to show loading indicator
      setSaving(true);
      
      // Log for debugging
      console.log('Publishing EPK...', epkData);
      
      // Create a deep copy of the current EPK data to avoid modifying the state directly
      const cleanedEPK = JSON.parse(JSON.stringify(epkData));
      
      // Process images that need uploading
      let updatedPhotos = Array.isArray(cleanedEPK.photos) ? [...cleanedEPK.photos] : [];
      let updatedLogo = cleanedEPK.logo ? {...cleanedEPK.logo} : null;
      let updatedThumbnail = cleanedEPK.thumbnail ? {...cleanedEPK.thumbnail} : null;
      
      // Upload photos that need it
      if (Array.isArray(updatedPhotos)) {
        for (let i = 0; i < updatedPhotos.length; i++) {
          const photo = updatedPhotos[i];
          if (photo.needsUpload) {
            try {
              // Get the actual file from our fileStorage ref using the photo's ID
              const photoFile = photo.fileId ? fileStorageRef.current.photos[photo.fileId] : null;
              
              if (!photoFile) {
                console.warn(`No file found for photo ID: ${photo.fileId}`);
                delete updatedPhotos[i].fileId;
                updatedPhotos[i].needsUpload = false;
                continue;
              }
              
              // Create form data for the upload
              const formData = new FormData();
              
              // Ensure we're passing the actual file
              if (photoFile instanceof File || photoFile instanceof Blob) {
                // Log file details before upload
                console.log(`Uploading photo ${i+1}/${updatedPhotos.length}:`, {
                  name: photo.fileName, 
                  size: (photoFile.size / 1024).toFixed(2) + 'KB',
                  type: photoFile.type
                });
                
                formData.append('media', photoFile, photo.fileName || 'photo.jpg');
                
                // Add type to indicate it's a photo
                formData.append('type', 'photo');
                
                // Upload the file with context=epk
                const uploadResponse = await fetch(`/api/processAndUploadMedia?context=epk`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${authToken}`
                  },
                  body: formData
                });
                
                if (!uploadResponse.ok) {
                  const errorText = await uploadResponse.text();
                  console.error(`Upload response error (${uploadResponse.status}):`, errorText);
                  throw new Error(`Failed to upload photo: ${photo.fileName}`);
                }
                
                const uploadResult = await uploadResponse.json();
                console.log('Photo upload result:', uploadResult);
                
                // Update the photo object with the uploaded data
                updatedPhotos[i] = {
                  ...photo,
                  url: uploadResult.url,
                  thumbnailUrl: uploadResult.thumbnailUrl || uploadResult.url,
                  filePath: uploadResult.filePath,
                  needsUpload: false
                };
                
                // Remove the fileId property as it's not needed anymore
                delete updatedPhotos[i].fileId;
                
                // If this photo is the thumbnail, update it too
                if (updatedThumbnail && updatedThumbnail.id === photo.id) {
                  updatedThumbnail = {
                    ...updatedThumbnail,
                    url: uploadResult.url,
                    thumbnailUrl: uploadResult.thumbnailUrl || uploadResult.url,
                    filePath: uploadResult.filePath,
                    needsUpload: false
                  };
                  
                  // Remove the fileId property from thumbnail too
                  delete updatedThumbnail.fileId;
                }
                
                // Revoke the old object URL to prevent memory leaks
                if (photo.url && photo.url.startsWith('blob:')) {
                  URL.revokeObjectURL(photo.url);
                }
              } else {
                console.error('Photo file is not a valid File or Blob object:', photoFile);
                console.warn(`Skipping upload for photo: ${photo.fileName || 'unnamed'}`);
                
                // Remove the invalid file reference
                delete updatedPhotos[i].fileId;
                updatedPhotos[i].needsUpload = false;
              }
            } catch (error) {
              console.error(`Error uploading photo ${i}:`, error);
              // Continue with other photos even if one fails
              
              // Remove the file reference
              delete updatedPhotos[i].fileId;
              updatedPhotos[i].needsUpload = false;
            }
          }
        }
      }
      
      // Upload logo if needed
      if (updatedLogo && updatedLogo.needsUpload) {
        try {
          // Get the actual logo file from our fileStorage ref
          const logoFile = fileStorageRef.current.logo;
          
          if (!logoFile) {
            console.warn('No logo file found in storage');
            delete updatedLogo.fileId;
            updatedLogo.needsUpload = false;
          } else if (logoFile instanceof File || logoFile instanceof Blob) {
            // Create form data for the upload
            const formData = new FormData();
            
            // Log logo file details
            console.log('Uploading logo:', {
              name: updatedLogo.fileName, 
              size: (logoFile.size / 1024).toFixed(2) + 'KB',
              type: logoFile.type
            });
            
            formData.append('media', logoFile, updatedLogo.fileName || 'logo.jpg');
            
            // Add type to indicate it's a photo (logo is treated as a photo)
            formData.append('type', 'photo');
            
            // Upload the file with context=epk
            const uploadResponse = await fetch(`/api/processAndUploadMedia?context=epk`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`
              },
              body: formData
            });
            
            if (!uploadResponse.ok) {
              const errorText = await uploadResponse.text();
              console.error(`Logo upload response error (${uploadResponse.status}):`, errorText);
              throw new Error(`Failed to upload logo: ${updatedLogo.fileName}`);
            }
            
            const uploadResult = await uploadResponse.json();
            console.log('Logo upload result:', uploadResult);
            
            // Update the logo object with the uploaded data
            updatedLogo = {
              ...updatedLogo,
              url: uploadResult.url,
              thumbnailUrl: uploadResult.thumbnailUrl || uploadResult.url,
              filePath: uploadResult.filePath,
              needsUpload: false
            };
            
            // Remove the fileId property as it's not needed anymore
            delete updatedLogo.fileId;
            
            // Revoke the old object URL to prevent memory leaks
            if (updatedLogo.url && updatedLogo.url.startsWith('blob:')) {
              URL.revokeObjectURL(updatedLogo.url);
            }
          } else {
            console.error('Logo file is not a valid File or Blob object:', logoFile);
            console.warn(`Skipping upload for logo: ${updatedLogo.fileName || 'unnamed'}`);
            
            // Remove the invalid file reference
            delete updatedLogo.fileId;
            updatedLogo.needsUpload = false;
          }
        } catch (error) {
          console.error('Error uploading logo:', error);
          // Continue even if logo upload fails
          
          // Remove the file reference
          delete updatedLogo.fileId;
          updatedLogo.needsUpload = false;
        }
      }
      
      // Create a copy of current EPK data with updated timestamp and images
      const updatedEPK = {
        ...cleanedEPK,
        photos: updatedPhotos,
        logo: updatedLogo,
        thumbnail: updatedThumbnail,
        updatedAt: new Date().toISOString(),
        isActive: true, // Mark as active when publishing
        userId: userData?.userId || '' // Ensure userId is included
      };
      
      // Call the onSave function passed from parent
      if (onSave) {
        // Wait for the result and check if it was successful
        const saveResult = await onSave(updatedEPK, true); // true means publishing
        
        // Log the result for debugging
        console.log('Save result:', saveResult);
        
        if (saveResult) {
          // Success case
          setLastSaved(new Date());
          
          // If creating new, move to preview step
          if (mode === 'create' && currentStep < steps.length - 1) {
            setCurrentStep(steps.length - 1);
          }
        } else {
          // Handle failure case - already showing alert in parent
          console.error('Failed to save EPK');
        }
      } else {
        console.error('No onSave function provided to EPKBuilder');
      }
    } catch (error) {
      console.error('Error saving EPK:', error);
      // Display an error message in the component itself
      alert('An error occurred while saving the EPK. Please check console for details.');
    } finally {
      setSaving(false);
    }
  };
  
  const handleExportPDF = async () => {
    // PDF export logic will be implemented here
    alert('PDF export feature will be implemented');
  };
  
  // Generate shareable link
  const handleShareableLink = () => {
    // Generate shareable link logic
    const shareableLink = `${window.location.origin}/epk/${userData.userName}`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareableLink)
      .then(() => {
        alert('Shareable link copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy link:', err);
        alert('Failed to copy link to clipboard.');
      });
  };
  
  // Update parent component when data changes
  const updateEPKData = useCallback((section, data) => {
    console.log(`Updating EPK data for section: ${section}`, data);
    
    setEPKData(prev => {
      // Create a new object to ensure React detects the change
      const updated = {...prev};
      
      // Handle different data structures based on section
      switch(section) {
        case 'basicInfo':
          // Copy all properties from data to the root level
          Object.keys(data).forEach(key => {
            updated[key] = data[key];
          });
          break;
          
        case 'photos':
          updated.photos = data.photos || [];
          updated.logo = data.logo || null;
          updated.thumbnail = data.thumbnail || null;
          break;
          
        case 'contact':
          updated.contact = data.contact || {};
          updated.socialLinks = data.socialLinks || {};
          break;
          
        case 'theme':
          updated.theme = data;
          break;
          
        case 'media':
          updated.music = data.music || {};
          updated.videos = data.videos || [];
          break;
          
        case 'stats':
          updated.stats = data.stats || {};
          updated.pressQuotes = data.pressQuotes || [];
          updated.pressCoverage = data.pressCoverage || [];
          break;
          
        default:
          // For array data or other fields, set directly
          updated[section] = Array.isArray(data) ? [...data] : {...data};
      }
      
      return updated;
    });
  }, []);
  
  // Navigate to next step
  const goToNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  // Navigate to previous step
  const goToPrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  // Navigate to a specific step
  const goToStep = (index) => {
    if (index >= 0 && index < steps.length) {
      setCurrentStep(index);
    }
  };
  
  // Validate the current step
  const validateStep = (step) => {
    console.log(`Validating step ${step}`, epkData);
    let stepErrors = {};
    let isValid = true;
    
    // Different validation for each step
    switch (step) {
      case 0: // Basic Info
        if (!epkData.artistName) {
          stepErrors.artistName = 'Artist name is required';
          isValid = false;
        }
        break;
        
      case 5: // Contact Info
        // Access the contact property correctly
        if (!epkData.contact?.email) {
          console.log("Email validation failed - missing email value:", epkData.contact);
          stepErrors.email = 'Contact email is required';
          isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(epkData.contact.email)) {
          // Basic email format validation
          stepErrors.email = 'Please enter a valid email address';
          isValid = false;
        }
        break;
        
      default:
        // Other steps don't have required fields
        break;
    }
    
    console.log('Validation result:', { step, errors: stepErrors, isValid });
    setErrors(stepErrors);
    return isValid;
  };
  
  // Handle step navigation with validation
  const handleNext = () => {
    if (validateStep(currentStep)) {
      handleAutoSave();
      goToNextStep();
    }
  };
  
  // Render the current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <BasicInfoStep 
            data={epkData}
            updateData={(data) => updateEPKData('basicInfo', data)}
            errors={errors}
            mode={mode}
          />
        );
      case 1:
        return (
          <PhotosStep 
            data={epkData}
            updateData={(data) => updateEPKData('photos', data)}
            errors={errors}
            mode={mode}
            authToken={authToken}
            fileStorageRef={fileStorageRef} // Pass the fileStorageRef to PhotosStep
          />
        );
      case 2:
        return (
          <MusicVideosStep 
            data={epkData}
            updateData={(data) => updateEPKData('media', data)}
            errors={errors}
            mode={mode}
          />
        );
      case 3:
        return (
          <PerformanceStep 
            data={epkData}
            updateData={(data) => updateEPKData('performances', data)}
            errors={errors}
            mode={mode}
          />
        );
      case 4:
        return (
          <StatsStep 
            data={epkData}
            updateData={(data) => updateEPKData('stats', data)}
            errors={errors}
            mode={mode}
          />
        );
      case 5:
        return (
          <ContactStep 
            data={epkData}
            updateData={(data) => updateEPKData('contact', data)}
            errors={errors}
            mode={mode}
          />
        );
      case 6:
        return (
          <ThemeStep 
            data={epkData}
            updateData={(data) => updateEPKData('theme', data)}
            errors={errors}
            mode={mode}
          />
        );
      case 7:
        return (
          <PreviewStep 
            data={epkData}
            mode={mode}
            onExportPDF={handleExportPDF}
            onShareableLink={handleShareableLink}
            onPublish={handleSaveAndPublish}
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  };
  
  if (!isOpen) return null;

  return (
    <div className={styles.epkBuilderOverlay}>
      <div className={styles.epkBuilderContainer}>
        <div className={styles.epkBuilderHeader}>
          <h2>{mode === 'view' ? 'Electronic Press Kit' : mode === 'edit' ? 'Edit EPK' : 'Create EPK'}</h2>
          
          {mode !== 'view' && (
            <div className={styles.saveStatus}>
              {autoSaving && (
                <span className={styles.autoSaving}>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Auto-saving...
                </span>
              )}
              
              {lastSaved && !autoSaving && (
                <span className={styles.lastSaved}>
                  <FontAwesomeIcon icon={faCheck} />
                  Last saved: {new Date(lastSaved).toLocaleTimeString()}
                </span>
              )}
            </div>
          )}
          
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        {mode !== 'view' && (
          <div className={styles.epkBuilderSteps}>
            <div className={styles.stepsProgress}>
              {steps.map((step, index) => (
                <div 
                  key={step.id}
                  className={`${styles.stepItem} ${currentStep === index ? styles.active : ''} ${currentStep > index ? styles.completed : ''}`}
                  onClick={() => mode !== 'view' && goToStep(index)}
                >
                  <div className={styles.stepIcon}>
                    <FontAwesomeIcon icon={step.icon} />
                  </div>
                  <span className={styles.stepName}>{step.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className={styles.epkBuilderContent}>
          {renderStepContent()}
        </div>
        
        {mode !== 'view' && (
          <div className={styles.epkBuilderFooter}>
            <div className={styles.footerButtons}>
              {currentStep > 0 && (
                <button 
                  className={styles.prevButton} 
                  onClick={goToPrevStep}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  Previous
                </button>
              )}
              
              {currentStep < steps.length - 1 ? (
                <button 
                  className={styles.nextButton} 
                  onClick={handleNext}
                >
                  Next
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              ) : (
                <button 
                  className={styles.publishButton} 
                  onClick={handleSaveAndPublish}
                  disabled={saving}
                  type="button" 
                >
                  {saving ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} />
                      Publish EPK
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EPKBuilder;